import {
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  SelectInput,
  TextInput,
  YesNoInput,
  FileInput,
  TextAreaInput,
} from './Inputs';
import FormattedMessages from '../../../../messages/admin/CustomerProfileMessages';

const Campaign = ({ profile, highlighted, enums, onChange, onDownload }) => {
  const [campaign, setCampaign] = useState({});
  const [useCases, setUseCases] = useState({});
  const [subUseCases, setSubUseCases] = useState({});

  useEffect(() => {
    setCampaign(profile?.campaign || {});
  }, [profile]);

  useEffect(() => {
    setUseCases(enums?.usecase || []);

    if (enums?.usecase) {
      const subUseCases = {};
      for (const [key, uc] of Object.entries(enums.usecase)) {
        if (uc.validSubUsecase) {
          subUseCases[key] = uc;
        }
      }
      setSubUseCases(subUseCases);
    }
  }, [enums]);

  const handleInputChange = (name, value, extra) => {
    const newValue = { ...campaign };

    if (name === 'sub_use_cases') {
      if (!newValue.sub_use_cases.value) {
        newValue.sub_use_cases.value = [];
      }

      if (extra === false) {
        newValue.sub_use_cases.value = newValue.sub_use_cases.value.filter(
          (subUseCase) => subUseCase !== value
        );
      } else {
        newValue.sub_use_cases.value = [...newValue.sub_use_cases.value, value];
      }
    } else {
      newValue[name].value = value;
    }

    if (name === 'use_case') {
      newValue.use_case.obj = useCases[value];
    }

    setCampaign(newValue);
    onChange('campaign', newValue);
  };

  return (
    <Grid container spacing={10} sx={{ width: '100%' }}>
      {/* Left Column */}
      <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
        <Stack spacing={3}>
          {campaign.use_case?.visible && (
            <SelectInput
              id="use_case"
              name="use_case"
              label={FormattedMessages.useCase}
              value={campaign.use_case?.value}
              required={campaign.use_case?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            >
              {Object.entries(useCases).map(([key, uc]) => (
                <MenuItem key={key} value={key}>
                  {uc.displayName}
                </MenuItem>
              ))}
            </SelectInput>
          )}

          {campaign.sub_use_cases?.visible && (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ md: 'flex-start' }}
            >
              <InputLabel
                sx={{ width: { md: 300 } }}
                required={campaign.sub_use_cases?.required}
              >
                {FormattedMessages.subUseCases}
              </InputLabel>

              <Stack>
                {Object.entries(subUseCases).map(([key, subUseCase]) => (
                  <FormControlLabel
                    size="small"
                    key={key}
                    control={
                      <Checkbox
                        value={key}
                        checked={campaign?.sub_use_cases?.value?.includes(key)}
                        onChange={(e) =>
                          handleInputChange(
                            'sub_use_cases',
                            key,
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={subUseCase.displayName}
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {campaign.message_description?.visible && (
            <TextInput
              id="message_description"
              name="message_description"
              label={FormattedMessages.messageDescription}
              value={campaign.message_description?.value}
              required={campaign.message_description?.required}
              error={campaign.message_description?.error}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.sample_1?.visible && (
            <TextInput
              id="sample_1"
              name="sample_1"
              label={FormattedMessages.messageSample}
              value={campaign.sample_1?.value || ''}
              required={campaign.sample_1?.required}
              error={campaign.sample_1?.error}
              onChange={handleInputChange}
              highlighted={highlighted}
              multiline
              rows={3}
            />
          )}

          {campaign.sample_2?.visible && (
            <TextInput
              id="sample_2"
              name="sample_2"
              label={FormattedMessages.messageSample2}
              value={campaign.sample_2?.value}
              required={campaign.sample_2?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
              multiline
              rows={3}
            />
          )}

          {campaign.sample_3?.visible && (
            <TextInput
              id="sample_3"
              name="sample_3"
              label={FormattedMessages.messageSample3}
              value={campaign.sample_3?.value}
              required={campaign.sample_3?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
              multiline
              rows={3}
            />
          )}

          {campaign.sample_4?.visible && (
            <TextInput
              id="sample_4"
              name="sample_4"
              label={FormattedMessages.messageSample4}
              value={campaign.sample_4?.value}
              required={campaign.sample_4?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
              multiline
              rows={3}
            />
          )}

          {campaign.sample_5?.visible && (
            <TextInput
              id="sample_5"
              name="sample_5"
              label={FormattedMessages.messageSample5}
              value={campaign.sample_5?.value}
              required={campaign.sample_5?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
              multiline
              rows={3}
            />
          )}

          {campaign.subscriber_opt_in?.visible && (
            <YesNoInput
              id="subscriber_opt_in"
              name="subscriber_opt_in"
              label={FormattedMessages.subscriberOptIn}
              value={campaign.subscriber_opt_in?.value}
              required={campaign.subscriber_opt_in?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.opt_in_keywords?.visible && (
            <TextInput
              id="opt_in_keywords"
              name="opt_in_keywords"
              label={FormattedMessages.optInKeywords}
              value={campaign.opt_in_keywords?.value}
              required={campaign.opt_in_keywords?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.opt_in_message?.visible && (
            <TextInput
              id="opt_in_message"
              name="opt_in_message"
              label={FormattedMessages.optInMessage}
              value={campaign.opt_in_message?.value}
              required={campaign.opt_in_message?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.subscriber_opt_out?.visible && (
            <YesNoInput
              id="subscriber_opt_out"
              name="subscriber_opt_out"
              label={FormattedMessages.subscriberOptOut}
              value={campaign.subscriber_opt_out?.value}
              required={campaign.subscriber_opt_out?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.opt_out_keywords?.visible && (
            <TextInput
              id="opt_out_keywords"
              name="opt_out_keywords"
              label={FormattedMessages.optOutKeywords}
              value={campaign.opt_out_keywords?.value}
              required={campaign.opt_out_keywords?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.opt_out_message?.visible && (
            <TextInput
              id="opt_out_message"
              name="opt_out_message"
              label={FormattedMessages.optOutMessage}
              value={campaign.opt_out_message?.value}
              required={campaign.opt_out_message?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.subscriber_help?.visible && (
            <YesNoInput
              id="subscriber_help"
              name="subscriber_help"
              label={FormattedMessages.subscriberHelp}
              value={campaign.subscriber_help?.value}
              required={campaign.subscriber_help?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.help_keywords?.visible && (
            <TextInput
              id="help_keywords"
              name="help_keywords"
              label={FormattedMessages.helpKeywords}
              value={campaign.help_keywords?.value}
              required={campaign.help_keywords?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.help_message?.visible && (
            <TextInput
              id="help_message"
              name="help_message"
              label={FormattedMessages.helpMessage}
              value={campaign.help_message?.value}
              required={campaign.help_message?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.embedded_link?.visible && (
            <YesNoInput
              id="embedded_link"
              name="embedded_link"
              label={FormattedMessages.embeddedLink}
              value={campaign.embedded_link?.value}
              required={campaign.embedded_link?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.embedded_link_sample?.visible && (
            <TextInput
              id="embedded_link_sample"
              name="embedded_link_sample"
              label={FormattedMessages.embeddedLinkSample}
              value={campaign.embedded_link_sample?.value}
              required={campaign.embedded_link_sample?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.embedded_phone?.visible && (
            <YesNoInput
              id="embedded_phone"
              name="embedded_phone"
              label={FormattedMessages.embeddedPhone}
              value={campaign.embedded_phone?.value}
              required={campaign.embedded_phone?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.embedded_phone_sample?.visible && (
            <TextInput
              id="embedded_phone_sample"
              name="embedded_phone_sample"
              label={FormattedMessages.embeddedPhoneSample}
              value={campaign.embedded_phone_sample?.value}
              required={campaign.embedded_phone_sample?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.number_pool?.visible && (
            <YesNoInput
              id="number_pool"
              name="number_pool"
              label={FormattedMessages.numberPool}
              value={campaign.number_pool?.value}
              required={campaign.number_pool?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.loan_agreements?.visible && (
            <YesNoInput
              id="loan_agreements"
              name="loan_agreements"
              label={FormattedMessages.loanAgreements}
              value={campaign.loan_agreements?.value}
              required={campaign.loan_agreements?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.affiliate_marketing?.visible && (
            <YesNoInput
              id="affiliate_marketing"
              name="affiliate_marketing"
              label={FormattedMessages.affiliateMarketing}
              value={campaign.affiliate_marketing?.value}
              required={campaign.affiliate_marketing?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.age_gated?.visible && (
            <YesNoInput
              id="age_gated"
              name="age_gated"
              label={FormattedMessages.ageGated}
              value={campaign.age_gated?.value}
              required={campaign.age_gated?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.service_agreement_url?.visible && (
            <TextInput
              id="service_agreement_url"
              name="service_agreement_url"
              label={FormattedMessages.serviceAgreementUrl}
              value={campaign.service_agreement_url?.value}
              required={campaign.service_agreement_url?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.privacy_policy_url?.visible && (
            <TextInput
              id="privacy_policy_url"
              name="privacy_policy_url"
              label={FormattedMessages.privacyPolicyUrl}
              value={campaign.privacy_policy_url?.value}
              required={campaign.privacy_policy_url?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.message_flow?.visible && (
            <TextInput
              id="message_flow"
              name="message_flow"
              label={FormattedMessages.messageFlow}
              value={campaign.message_flow?.value}
              required={campaign.message_flow?.required}
              onChange={handleInputChange}
              highlighted={highlighted}
            />
          )}

          {campaign.message_flow_files?.visible && (
            <FileInput
              id="message_flow_files"
              name="message_flow_files"
              multiple
              label={FormattedMessages.messageFlowFiles}
              value={campaign.message_flow_files?.value}
              required={campaign.message_flow_files?.required}
              onChange={handleInputChange}
              onDownload={onDownload}
              highlighted={highlighted}
            />
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default Campaign;
